import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Box, Divider, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Menu, MenuItem } from '@material-ui/core';
import UserBadge from 'components/threepp/UserBadge/UserBadge';
import AssessmentProgressBar from 'components/threepp/AssessmentProgressBar/AssessmentProgressBar';
import EventEmitter from 'utils/events';
import { EventEmitterTypes } from 'utils/constants';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    marginTop: '1rem',
    padding: '0px 16px'
  },
  name: {
    fontSize: 14,
    fontWeight: 500,
    color: '#212121'
  },
  position: {
    fontSize: 13,
    color: '#000000'
  },
  participation: {
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    float: 'right',
    borderRadius: '4px',
    paddingTop: '2px',
    paddingLeft: '7px',
    paddingRight: '7px',
    paddingBottom: '2px',
    backgroundColor: '#EDFEFF',
    color: '#176DC2'
  },
  percent: {
    fontSize: 13,
    display: 'flex',
    justifyContent: 'flex-end',
    fontWeight: 600
  },
  more: {
    display: 'flex',
    justifyContent: 'flex-end',
    fontSize: '1.25rem'
  },
  completion: {
    fontSize: 13,
    color: '#000000',
    paddingBottom: '1rem'
  },
  moreIcon: {
    width: '20px !important',
    height: '20px !important'
  },
  moreIconButton: {
    '&.Mui-focusVisible': {
      background: theme?.palette?.icon?.iconHoverColor
    },
    '&:hover': {
      background: theme?.palette?.icon?.iconHoverColor
    }
  }
}));

export const ParticipantDetail = ({ participant, currentUser, isOwner, disabled, ownersCount, onDeleteUser, onUpdateUser, isOnline }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const inputRef = useRef();
  const isCurrentUser = participant.email === currentUser.email;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  let disableMenu = true;

  if (disabled !== true) {
    // only OWNERS can delete other users
    if (isOwner === true) {
      if (participant.isOwner === false) {
        // no restrictions to remove collaborators
        disableMenu = false;
      } else if (ownersCount > 1) {
        // only allow delete owners if there are at least 2
        disableMenu = false;
      }
    } else if (isCurrentUser) {
      // contributors can remove themselves from an assessment
      disableMenu = false;
    }
  }

  const modalClosedCallBack = email => {
    if (participant.email === email) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    EventEmitter.subscribe(EventEmitterTypes.FOCUS_PARTICIPANT_OPTION, ({ email }) => modalClosedCallBack(email));
    return () => {
      EventEmitter.unsubscribe(EventEmitterTypes.FOCUS_PARTICIPANT_OPTION);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <UserBadge user={participant} noName isOnline={isOnline} />
        </Grid>
        <Grid item xs={10} sm container>
          <Grid item xs={9}>
            <Typography className={classes.name}>
              {' '}
              {participant.fullName}
              {isCurrentUser && ` (${t('common.me')})`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Box component="span" className={classes.more}>
              <IconButton
                className={classes.moreIconButton}
                size="small"
                aria-label="more"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                ref={inputRef}
                disableRipple
              >
                <MoreHorizIcon className={classes.moreIcon} />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClick={handleClose}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button'
                }}
                PaperProps={{
                  style: {
                    transform: 'translateX(-15px) translateY(40px)'
                  }
                }}
              >
                <MenuItem
                  data-testid="participantsPanel-editParticipant"
                  className="role-option"
                  onClick={() => {
                    onUpdateUser(participant);
                    handleClose();
                  }}
                  disabled={disableMenu}
                >
                  {t('assessment.participantMenu.editRole')}
                </MenuItem>
                <MenuItem
                  data-testid="participantsPanel-removeParticipant"
                  className="remove-option"
                  onClick={() => {
                    onDeleteUser(participant);
                    handleClose();
                  }}
                  disabled={disableMenu}
                >
                  {t('assessment.participantMenu.removeParticipant')}
                </MenuItem>
              </Menu>
            </Box>
          </Grid>
          <Grid item xs={9}>
            <Typography className={classes.position}> {participant.position} </Typography>
          </Grid>
          {participant.isOwner && (
            <Grid item xs={3}>
              <div className={classes.participation}>
                <Box component="span">Owner</Box>
              </div>
            </Grid>
          )}
          <Grid item xs={12}>
            <AssessmentProgressBar className="col-completed" answered={participant.answered} total={participant.total} isMobile />
          </Grid>
          <Grid item xs={9}>
            <Typography className={classes.completion}>
              {participant.answered}/{participant.total} {t('common.complete')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography className={classes.percent}> {participant.complete}% </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
    </div>
  );
};
